import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Alert,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useOfficialDailyDetailController } from './context/official-daily-detail-controller';
import { useOfficialDailyDetailState } from './context/official-daily-detail-context';

export function OfficialDailyDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = useOfficialDailyDetailController();
  const { detail, loading, error } = useOfficialDailyDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(Number(id));
    }
  }, [id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleDownload = () => {
    if (detail?.fileDownloadUrl) {
      const link = document.createElement('a');
      link.href = detail.fileDownloadUrl;
      link.download = `diario-oficial-edicao-${detail.editionNumber}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewPDF = () => {
    if (detail?.fileViewUrl) {
      window.open(detail.fileViewUrl, '_blank');
    }
  };

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={2} />;
  }

  if (error || !detail) {
    return (
      <PageContainer>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || 'Edição do Diário Oficial não encontrada'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/diario-oficial')}
            sx={{ mt: 2 }}
          >
            Voltar para listagem
          </Button>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl">
        <PageHeader
          title={`Edição Nº ${detail.editionNumber}/${detail.year}`}
          subtitle="Detalhes da Edição do Diário Oficial"
          breadcrumbs={[
            { label: 'Edições do Diário Oficial', href: '/diario-oficial' },
            { label: `Edição Nº ${detail.editionNumber}/${detail.year}` },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarTodayIcon color="primary" />
                <Typography variant="h6" color="primary">
                  Informações da Edição
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Edição:</strong> {detail.editionNumber}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Ano:</strong> {detail.year}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Data de Publicação:</strong> {formatDate(detail.publicationDate)}
                </Typography>
                {detail.closedOn && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Data de Fechamento:</strong> {formatDate(detail.closedOn)}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Total de Páginas:</strong> {detail.lastPage}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DescriptionIcon color="primary" />
                <Typography variant="h6" color="primary">
                  Status e Ações
                </Typography>
              </Box>
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={detail.isClosed ? 'Fechada' : 'Aberta'}
                    color={detail.isClosed ? 'default' : 'success'}
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={detail.isVisible ? 'Visível' : 'Oculta'}
                    color={detail.isVisible ? 'primary' : 'warning'}
                  />
                </Box>
                {detail.authenticityCode && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <strong>Código de Autenticidade:</strong> {detail.authenticityCode}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                  <Button
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleViewPDF}
                    disabled={!detail.fileViewUrl}
                    fullWidth
                  >
                    Visualizar PDF
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={!detail.fileDownloadUrl}
                    fullWidth
                  >
                    Baixar PDF
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Publicações */}
        {detail.publications && detail.publications.length > 0 && (
          <>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
              Publicações desta Edição ({detail.publications.length})
            </Typography>

            <Grid container spacing={3}>
              {detail.publications.map((publication, index) => (
                <Grid item xs={12} key={publication.id}>
                  <Card elevation={2} sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                          {publication.title || `Publicação ${index + 1}`}
                        </Typography>
                        {publication.category && (
                          <Chip label={publication.category} size="small" color="primary" variant="outlined" />
                        )}
                      </Box>

                      {publication.numberingCode && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Código:</strong> {publication.numberingCode}
                        </Typography>
                      )}

                      {publication.publishingAgency && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Órgão Publicador:</strong> {publication.publishingAgency}
                        </Typography>
                      )}

                      {publication.publicationDate && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Data:</strong> {formatDate(publication.publicationDate)}
                        </Typography>
                      )}

                      {publication.observation && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          {publication.observation}
                        </Alert>
                      )}

                      {publication.isRepublication && (
                        <Chip
                          label="Republicação"
                          size="small"
                          color="warning"
                          sx={{ mt: 2 }}
                        />
                      )}

                      {/* Botões de Download e Visualização da Publicação */}
                      {(publication.fileViewUrl || publication.fileDownloadUrl) && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PictureAsPdfIcon />}
                              onClick={() => {
                                const url = publication.fileViewUrl;
                                if (url) window.open(url, '_blank');
                              }}
                              disabled={!publication.fileViewUrl}
                              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
                            >
                              Visualizar Publicação
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => {
                                const url = publication.fileDownloadUrl;
                                if (url) {
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `publicacao-${publication.id || index + 1}.pdf`;
                                  link.target = '_blank';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              }}
                              disabled={!publication.fileDownloadUrl}
                              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
                            >
                              Baixar Publicação
                            </Button>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/diario-oficial')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Edições do Diário Oficial
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
